import _                                           from 'underscore';
import React, { FC, useContext, useEffect }        from 'react';
import { Header, Icon, LabelProps, List, Segment } from 'semantic-ui-react';
import { Cell, Grid }                              from 'styled-css-grid';

import { CorvalProjectActivity, CorvalProjectSubObjective, CorvalSubObjective, Size, SystemEvent, Uuid } from '../../../../../types';

import { DecisionEngineQueries }                                                                  from '../../../../../queries';
import { useFeatureFlags, useProjectObjectives, useSidebar }                                      from '../../../../../hooks';
import { runIfFunction }                                                                          from '../../../../../util';
import { ENTITY_ICONS }                                                                           from '../../../../../constants/ProductEnums';
import { CorvalDeepContentSidebar, SidebarContainer, SidebarDeepContentBackBar, StyledContainer } from '../../../../containers';
import { ItemFromQuery }                                                                          from '../../../../components/from-query/ItemFromQuery';
import { FinePrintMessage }                                                                       from '../../../../components/util/messaging/FinePrintMessage';
import { CaveatMessage }                                                                          from '../../../../components/util/messaging/CaveatMessage';
import { Tooltip }                                                                                from '../../../../components/util/messaging/ToolTip';
import { StringOrEmpty }                                                                          from '../../../../components/display/StringOrEmpty';
import { EntityNote }                                                                             from '../../../../components/annotations/EntityNote';
import { HumanizedDateRangeLabel }                                                                from '../../../../components/labels/HumanizedDateRangeLabel';
import { EntityDetailMilestone }                                                                  from '../../common/entities/EntityDetailMilestone';
import { ProjectActivityDetails }                                                                 from '../../activities/ProjectActivityDetails';
import { ProjectContext }                                                                         from '../../ProjectIndexPage';
import { ObjectiveProgressionIndicator }                                                          from '../progression/ObjectiveProgressionIndicator';
import { ObjectiveDetailEstimatedTiming }                                                         from './ObjectiveDetailEstimatedTiming';
import { ObjectiveDetailProjectEstimatedTiming, ObjectiveDetailProjectEstimatedTimingChildProps } from './ObjectiveDetailProjectEstimatedTiming';
import { ObjectiveDetailRelatedProjectActivityList }                                              from './ObjectiveDetailRelatedProjectActivityList';
import { ObjectiveDetailRelatedActivityList }                                                     from './ObjectiveDetailRelatedActivityList';
import { ObjectiveDetailRelatedProjectActivityTagCloud }                                          from './ObjectiveDetailRelatedProjectActivityTags';
import { ProjectQueries }                                                                         from "@src/queries/ProjectQueries";


export interface ObjectiveDetailsProps {

    /* The subobjective subObjectiveId to retrieve */
    id: Uuid,

    projectId?: Uuid,

    organizationId?: Uuid,

    /* Flag to get project timing and other specific information */
    projectDetails?: boolean,

    /* Flag to indicate whether sample activities listed are within the same category or not */
    categoryActivitiesOnly?: boolean,

    /* Max activity count to display */
    activityCount?: number,

    /* Allow activity links or not */
    includeActivityLinks?: boolean,

    /* Allow clicking on objective timing */
    canEditObjectiveTiming?: boolean

    /* Callback on objective timing click */
    onObjectiveTimingClicked?( e: SystemEvent, subobjective: CorvalProjectSubObjective ): void,

    onTagClick?: ( index: number, tag: string ) => void,

    showHeader?: boolean,
    showDescription?: boolean,
    showProgress?: boolean,
    showObjectiveGroup?: boolean,
    showCategory?: boolean,
    showSubcategory?: boolean,
    showMilestone?: boolean,
    showDuration?: boolean,
    showActivities?: boolean,
    showTooltips?: boolean,
    showAnnotations?: boolean,
    showTags?: boolean,
    showAuditEntries?: boolean,
    nestedContent?: number,
}

const defaultProps: ObjectiveDetailsProps = {
    id:                       null,
    projectId:                null,
    organizationId:           null,
    projectDetails:           false,
    categoryActivitiesOnly:   false,
    activityCount:            7,
    includeActivityLinks:     true,
    canEditObjectiveTiming:   false,
    onObjectiveTimingClicked: null,
    onTagClick:               null,
    showHeader:               true,
    showDescription:          true,
    showProgress:             true,
    showObjectiveGroup:       false,
    showCategory:             true,
    showSubcategory:          true,
    showMilestone:            true,
    showDuration:             true,
    showActivities:           true,
    showTooltips:             true,
    showAnnotations:          true,
    showTags:                 true,
    showAuditEntries:         true,
    nestedContent:            0,
}

const ObjectiveDetails: FC<ObjectiveDetailsProps> = ( props ) => {

          let projectContext         = useContext( ProjectContext ),
              projectId              = projectContext.projectId ?? props.projectId ?? null,
              organizationId         = projectContext.organizationId ?? props.organizationId ?? null,
              { isMilestoneAligned } = useProjectObjectives( projectId );

          let featureFlags = useFeatureFlags();

          let secondaryContentSidebar       = useSidebar(),
              [ , secondaryContentActions ] = secondaryContentSidebar;

          useEffect( () => {

              if( props.nestedContent > 0 ){
                  console.log( { 'nested objective': props.nestedContent } );
              }

          }, [] )

          const handleSecondaryContentClicked = ( item: CorvalProjectActivity ) => {

              secondaryContentActions.show(
                  <SidebarContainer padded={ false } cellRows={ `auto 1fr` }>

                      <SidebarDeepContentBackBar
                          label={ 'Back to Objective' }
                          onClick={ () => secondaryContentActions.toggle( false ) }
                      />

                      <ProjectActivityDetails
                          id={ item.id }
                          projectId={ projectId }
                          organizationId={ organizationId }
                          showCriticality={ featureFlags.enableProjectActivityCriticalityViewsAndTools }
                          showTags={ props.showTags && featureFlags.enableProjectActivityCriticalityViewsAndTools }
                          onTagClick={ props.onTagClick }
                          showSupportingObjective={ false }
                          showWatchers={ false }
                          includeObjectiveLinks={ true }
                          showParentDependencies={ true }
                          includeParentDependencyLinks={ true }
                          canEditRecurrences={ featureFlags.enableProjectActivityRecurrenceEditing }
                          showProgress={ featureFlags.enableProjectActivityStatus && props.showProgress }
                          nestedContent={ props.nestedContent + 1 }
                      />

                  </SidebarContainer>, {
                      width: 'very wide',
                  }
              )
          }

          const handleTagClicked = ( index, content ) => {

              runIfFunction( props.onTagClick, index, content );
          }

          return (

              <ItemFromQuery
                  as={ React.Fragment }
                  query={ DecisionEngineQueries.queries.GET_SUBOBJECTIVE( `
                        id
                        displayName
                        description
                        objective { id displayName description }
                        targetIntakeQuestionId
                        category { id displayName }
                        subCategory { id displayName }
                        applicableStartYear
                        projectTimingMilestone
                        projectTimingMilestoneOffsetMonths
                        conditions {
                            id
                            conditionType
                            value
                        }
                  ` ) }
                  queryOptions={ {
                      variables:   {
                          id: props.id
                      },
                      fetchPolicy: 'network-only'
                  } }
                  queryResultObject={ data => data.subObjective }>

                  { ( subobjective: CorvalSubObjective ) => {

                      let {
                              id:          subObjectiveId,
                              objective:   { displayName: objectiveGroupName },
                              category:    { id: categoryId, displayName: categoryName },
                              subCategory: { id: subCategoryId, displayName: subCategoryName },
                              displayName,
                              description,
                              targetIntakeQuestionId,
                              applicableStartYear,
                              projectTimingMilestone,
                              projectTimingMilestoneOffsetMonths
                          } = subobjective;

                      // if project details, and duration is clickable, display accordingly
                      // and allow user to do props.onObjectiveTimingClicked
                      let clickableDurationProps: LabelProps = {
                          onClick:   ( e: SystemEvent ) => props.onObjectiveTimingClicked( e, subobjective as unknown as CorvalProjectSubObjective ),
                          className: 'cursor--pointer',
                          color:     'red',
                      }

                      return (

                          <CorvalDeepContentSidebar
                              key={ props.id }
                              sidebar={ secondaryContentSidebar }
                              inset={ [ 10, 0, 0, ( 20 * ( props.nestedContent + 1 ) ) ] }>

                              <StyledContainer padding={ Size.MD }>

                                  <Segment basic>

                                      <Grid columns={ `${ ( props.projectDetails && props.showProgress ) ? '32px' : 'auto' } auto` } rowGap={ '20px' } columnGap={ '20px' }>


                                          {/* Progression tools */ }
                                          { props.projectDetails && props.showProgress && (
                                              <Cell>

                                                  <ObjectiveProgressionIndicator
                                                      id={ props.id }
                                                      showPopupHeader={ false }
                                                      showPercentageComplete={ false }
                                                      allowUpdate={ true }
                                                      size={ 32 }
                                                  />
                                              </Cell>
                                          ) }

                                          <Cell>

                                              {/* Header */ }
                                              { props.showHeader && ( <Header as={ 'h3' } content={ displayName }/> ) }

                                              {/* Description */ }
                                              { props.showDescription && <StringOrEmpty evaluate={ description } passValue={ <FinePrintMessage>{ description }</FinePrintMessage> } emptyValue={ null }/> }

                                              {/* Tags */ }
                                              { props.showTags && props.projectDetails && (
                                                  <ObjectiveDetailRelatedProjectActivityTagCloud
                                                      size={ 'tiny' }
                                                      id={ subObjectiveId }
                                                      onClick={ _.isFunction( props.onTagClick ) ? handleTagClicked : null }
                                                  />
                                              ) }

                                          </Cell>

                                      </Grid>

                                  </Segment>

                                  {/* Project specific activities */ }
                                  { props.showActivities && props.projectDetails && (

                                      <Segment basic className={ 'peek-container' }>
                                          <Header as={ 'h4' } dividing>
                                              { 'Project Activities' }
                                              { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'These activities are available on your project as part of this objective\'s inititive.' }/></span> }
                                          </Header>
                                          <ObjectiveDetailRelatedProjectActivityList
                                              id={ subObjectiveId }
                                              onClick={ props.includeActivityLinks ? handleSecondaryContentClicked : null }
                                          />
                                      </Segment>

                                  ) }

                                  {/* General Activities Listing */ }
                                  { props.showActivities && !props.projectDetails && (

                                      <Segment basic className={ 'peek-container' }>
                                          <Header as={ 'h4' } dividing>
                                              { `Sample Activities ${ props.categoryActivitiesOnly ? 'in Category' : '' }` }
                                              { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'These category-specific activities may be suggested for projects with this objective to help further strategic goals.' }/></span> }
                                          </Header>
                                          <ObjectiveDetailRelatedActivityList
                                              id={ subObjectiveId }
                                              activityCount={ props.activityCount }
                                              categoryId={ props.categoryActivitiesOnly ? categoryId : null }
                                              subCategoryId={ props.categoryActivitiesOnly ? subCategoryId : null }
                                          />
                                      </Segment>

                                  ) }

                                  <Segment basic>

                                      <Header as={ 'h4' } dividing>Additional Details</Header>

                                      <List verticalAlign={ 'top' } relaxed divided>

                                          { props.showObjectiveGroup && (
                                              <List.Item className={ 'peek-container' }>

                                                  <List.Content>

                                                      <Grid columns={ `auto 1fr auto` }>
                                                          <Cell>

                                                              <Icon name={ ENTITY_ICONS.OBJECTIVES.GROUPS } color={ 'purple' }/>

                                                          </Cell>
                                                          <Cell>

                                                              <List.Header>Objective Group: { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'The group this objective is thematically related to.' }/></span> }</List.Header>

                                                          </Cell>
                                                          <Cell>

                                                              <List.Description>{ objectiveGroupName }</List.Description>

                                                          </Cell>

                                                      </Grid>
                                                  </List.Content>
                                              </List.Item>
                                          ) }

                                          { props.showMilestone && (
                                              <>
                                                  { props.projectDetails ? (

                                                      <ItemFromQuery
                                                          as={ React.Fragment }
                                                          query={ DecisionEngineQueries.queries.GET_PROJECT_SUBOBJECTIVES(
                                                              ` id
                                                                projectTimingMilestoneOffsetMonthsOverride
                                                                projectTimingMilestoneOverride
                                                                projectCustomTimingMilestoneId
                                                                startTimeUtc
                                                                subObjective {
                                                                    id
                                                                    projectTimingMilestone
                                                                    projectTimingMilestoneOffsetMonths
                                                                 }
                                                                 project {
                                                                     ${ ProjectQueries.fragments.PROJECTS.TIMELINE_FIELDS_AND_DEFINED_TIMINGS() }
                                                                 }
                                                            ` ) }
                                                          queryOptions={ {
                                                              variables:   {
                                                                  projectId,
                                                                  where: {
                                                                      subObjectiveId: {
                                                                          eq: props.id
                                                                      }
                                                                  }
                                                              },
                                                              fetchPolicy: 'network-only'
                                                          } }
                                                          queryResultObject={ data => {

                                                              return ( data ) ? _.first( data.projectSubObjectives ) ?? {} : {}

                                                          } }
                                                          errorComponent={ ( errors, query ) => (
                                                              <Segment size={ 'small' } basic secondary>
                                                                  <FinePrintMessage>Milestone information unavailable.</FinePrintMessage>
                                                              </Segment>
                                                          ) }>

                                                          { ( projectSubObjective: CorvalProjectSubObjective ) => {

                                                              let {
                                                                      projectTimingMilestoneOverride             = null,
                                                                      projectCustomTimingMilestoneId             = null,
                                                                      projectTimingMilestoneOffsetMonthsOverride = null,
                                                                      startTimeUtc,
                                                                      project,
                                                                      subObjective,
                                                                  } = projectSubObjective;

                                                              let {
                                                                      projectTimingMilestone             = null,
                                                                      projectTimingMilestoneOffsetMonths = null,
                                                                  } = subObjective;

                                                              // Checks to see if a milestone exists

                                                              const hasNoMilestone = ( !projectCustomTimingMilestoneId &&
                                                                                       !projectTimingMilestoneOverride &&
                                                                                       !projectTimingMilestone )

                                                              // If a milestone exists, checks to see if it's aligned with the Timeframe

                                                              const hasMilestoneAlignment = isMilestoneAligned( projectSubObjective, project );

                                                              // If no milestone exists or they aren't aligned with the timeframe ,  render nothing
                                                              if( hasNoMilestone || !hasMilestoneAlignment ){

                                                                  return (
                                                                      <List.Item className={ 'peek-container' }>
                                                                          <List.Content>
                                                                              <Grid columns={ `auto 1fr minmax(auto, 65%)` }>
                                                                                  <Cell>
                                                                                      <Icon name={ ENTITY_ICONS.MILESTONES.ENTITY } color={ 'purple' }/>
                                                                                  </Cell>
                                                                                  <Cell>
                                                                                      <List.Header>Milestone:</List.Header>
                                                                                  </Cell>
                                                                                  <Cell className={ 'text-right' }>
                                                                                      <List.Description>
                                                                                          <FinePrintMessage>The timespan of this objective depends on an activity in another objective. Click the first activity to view its prerequisite and determine the start time.</FinePrintMessage>
                                                                                      </List.Description>
                                                                                  </Cell>
                                                                              </Grid>
                                                                          </List.Content>
                                                                      </List.Item>
                                                                  );
                                                              }

                                                              let milestoneField = projectCustomTimingMilestoneId ?? projectTimingMilestoneOverride ?? projectTimingMilestone ?? null,
                                                                  offsetMonths   = projectTimingMilestoneOffsetMonthsOverride ?? projectTimingMilestoneOffsetMonths;

                                                              return (
                                                                  <List.Item className={ 'peek-container' }>

                                                                      <List.Content>
                                                                          <Grid columns={ `auto 1fr minmax(auto, 65%)` }>
                                                                              <Cell>

                                                                                  <Icon name={ ENTITY_ICONS.MILESTONES.ENTITY } color={ 'purple' }/>

                                                                              </Cell>
                                                                              <Cell>

                                                                                  <List.Header>Milestone: { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'Key milestone associated with this objective.' }/></span> }</List.Header></Cell>

                                                                              <Cell className={ 'text-right' }>

                                                                                  <List.Description>
                                                                                      <List className={ 'no-padding-left' } relaxed={ 'very' } style={ { paddingTop: 0, paddingBottom: 0 } }>

                                                                                          <List.Item>

                                                                                              <EntityDetailMilestone
                                                                                                  projectId={ projectId }
                                                                                                  fieldId={ milestoneField }
                                                                                                  showDate={ true }
                                                                                                  offset={ offsetMonths }
                                                                                                  popupProps={ {
                                                                                                      position: 'top right'
                                                                                                  } }
                                                                                              />

                                                                                          </List.Item>

                                                                                      </List>

                                                                                  </List.Description>

                                                                              </Cell>
                                                                          </Grid>
                                                                      </List.Content>
                                                                  </List.Item>
                                                              )
                                                          } }

                                                      </ItemFromQuery>

                                                  ) : (

                                                        projectTimingMilestone && (
                                                            <List.Item className={ 'peek-container' }>

                                                                <List.Content>
                                                                    <Grid columns={ `auto 1fr minmax(auto, 65%)` }>
                                                                        <Cell>

                                                                            <Icon name={ ENTITY_ICONS.MILESTONES.ENTITY } color={ 'purple' } className={ 'rotate--45' }/>

                                                                        </Cell>
                                                                        <Cell>

                                                                            <List.Header>Milestone: { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'Key milestone associated with this objective.' }/></span> }</List.Header>

                                                                        </Cell>
                                                                        <Cell className={ 'text-right' }>

                                                                            <List.Description>

                                                                                <List className={ 'no-padding-left' } relaxed={ 'very' } style={ { paddingTop: 0, paddingBottom: 0 } }>

                                                                                    <List.Item>

                                                                                        <EntityDetailMilestone
                                                                                            projectId={ null }
                                                                                            fieldId={ projectTimingMilestone }
                                                                                            showDate={ false }
                                                                                            offset={ projectTimingMilestoneOffsetMonths }
                                                                                            popupProps={ {
                                                                                                position: 'top right'
                                                                                            } }
                                                                                        />

                                                                                    </List.Item>

                                                                                </List>

                                                                            </List.Description>

                                                                        </Cell>
                                                                    </Grid>
                                                                </List.Content>
                                                            </List.Item>
                                                        )

                                                    ) }
                                              </>
                                          ) }

                                          { props.showDuration && (
                                              <List.Item className={ 'peek-container' }>

                                                  <List.Content>
                                                      <Grid columns={ `auto minmax(90px, 1fr) auto` }>
                                                          <Cell middle>

                                                              <Icon name={ 'clock outline' } color={ 'purple' }/>

                                                          </Cell>
                                                          <Cell middle>

                                                              <List.Header>Timeframe: { props.showTooltips &&
                                                                                        <span className={ 'peek-item' }><Tooltip content={ 'The timeframe in which this objective will fall on your project’s timeline based on other information you’ve provided.' }/></span> }</List.Header>

                                                          </Cell>


                                                          { props.projectDetails && (
                                                              <ObjectiveDetailProjectEstimatedTiming id={ subObjectiveId }>

                                                                  { ( components: ObjectiveDetailProjectEstimatedTimingChildProps ) => {

                                                                      let { dateRangeComponent, humanizedDateRangeComponent, auditComponent, startTime, endTime } = components;

                                                                      return (
                                                                          <>
                                                                              <Cell middle style={ { flexFlow: 'nowrap', alignItems: 'center' } }>
                                                                                  { props.canEditObjectiveTiming
                                                                                    ? <HumanizedDateRangeLabel
                                                                                        withSummaryLabel={ false }
                                                                                        startDate={ startTime }
                                                                                        endDate={ endTime }
                                                                                        fromLabelProps={ clickableDurationProps }
                                                                                        toLabelProps={ clickableDurationProps }
                                                                                    />
                                                                                    : dateRangeComponent
                                                                                  }
                                                                              </Cell>
                                                                              <Cell left={ 3 } className={ 'text-right' }>
                                                                                  { humanizedDateRangeComponent }
                                                                              </Cell>
                                                                              <Cell width={ 3 }>
                                                                                  { auditComponent }
                                                                              </Cell>
                                                                          </>
                                                                      )
                                                                  } }

                                                              </ObjectiveDetailProjectEstimatedTiming>
                                                          ) }

                                                          { !props.projectDetails && (
                                                              <>
                                                                  <Cell middle>
                                                                      <List.Description>
                                                                          <ObjectiveDetailEstimatedTiming id={ subObjectiveId }/>
                                                                      </List.Description>
                                                                  </Cell>
                                                                  <Cell left={ 2 } width={ 2 }>
                                                                      <FinePrintMessage>
                                                                          <CaveatMessage caveat={ 'This timing is an estimate.' } whatMightChange={ 'this timing' }/>
                                                                      </FinePrintMessage>
                                                                  </Cell>
                                                              </>
                                                          ) }
                                                      </Grid>
                                                  </List.Content>
                                              </List.Item>
                                          ) }

                                          { props.showCategory && (
                                              <List.Item className={ 'peek-container' }>

                                                  <List.Content>
                                                      <Grid columns={ `auto 1fr 1fr` }>
                                                          <Cell><Icon name={ 'tag' } color={ 'purple' }/></Cell>
                                                          <Cell><List.Header>Category: { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'Categories describe the overall related set of activities your team will undertake to meet this objective.' }/></span> }</List.Header></Cell>
                                                          <Cell><List.Description>{ categoryName }</List.Description></Cell>
                                                      </Grid>
                                                  </List.Content>
                                              </List.Item>
                                          ) }

                                          { props.showSubcategory && (
                                              <List.Item className={ 'peek-container' }>

                                                  <List.Content>
                                                      <Grid columns={ `auto 1fr 1fr` }>
                                                          <Cell><Icon name={ 'tag' } color={ 'purple' }/></Cell>
                                                          <Cell><List.Header>Subcategory: { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'Subcategories further describe the related groups of activities your team will undertake to meet this objective.' }/></span> }</List.Header></Cell>
                                                          <Cell><List.Description>{ subCategoryName }</List.Description></Cell>
                                                      </Grid>
                                                  </List.Content>
                                              </List.Item>
                                          ) }

                                          { props.showAnnotations && props.projectDetails && (
                                              <List.Item>
                                                  <List.Content>

                                                      <ItemFromQuery
                                                          as={ React.Fragment }
                                                          query={ DecisionEngineQueries.queries.GET_PROJECT_SUBOBJECTIVES( `id projectTimingMilestoneOverride` ) }
                                                          queryOptions={ {
                                                              variables:   {
                                                                  projectId,
                                                                  where: {
                                                                      subObjectiveId: {
                                                                          eq: props.id
                                                                      }
                                                                  }
                                                              },
                                                              fetchPolicy: 'network-only'
                                                          } }
                                                          queryResultObject={ data => _.first( data.projectSubObjectives ) ?? {} }>

                                                          { ( projectSubObjective: CorvalProjectSubObjective ) => {

                                                              return <EntityNote id={ projectSubObjective.id } type={ 'projectObjective' } showLastEditedOnNote={ true }/>
                                                          } }

                                                      </ItemFromQuery>

                                                  </List.Content>
                                              </List.Item>
                                          ) }

                                      </List>

                                  </Segment>

                              </StyledContainer>

                          </CorvalDeepContentSidebar>
                      )
                  } }

              </ItemFromQuery>
          )
              ;
      }
;

ObjectiveDetails.defaultProps = defaultProps;

export { ObjectiveDetails }
